import * as React from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { Box, InputAdornment, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';

import { colors } from 'styles/colors';
import { fonts } from 'styles/fonts';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import { ReactNode } from 'react';
import useGlobalStyles from 'styles/globalStyles';

type DateRangeValue = [Dayjs | null, Dayjs | null];

type CustomDateRangePickerProps = {
  name: string;
  control: Control<any>;
  error?: FieldError;
  placeholder?: string;
  label?: string;
  required?: boolean;
  note?: string;
  sx?: any;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
  floatingLabel?: string;
};

export default function CustomDateRangePicker({
  name,
  control,
  error,
  placeholder,
  label,
  required,
  note,
  sx,
  endIcon,
  startIcon,
  floatingLabel,
}: CustomDateRangePickerProps) {
  const globalStyles = useGlobalStyles();
  const [openStart, setOpenStart] = React.useState(false);
  const [openEnd, setOpenEnd] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <Box>
      {label && (
        <CustomLabel
          label={label}
          note={note}
          required={required}
        />
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const [start, end] = (field.value ??
            [null, null]) as DateRangeValue;

          const displayValue =
            start && end
              ? `${dayjs(start).format('DD MMM, YYYY')} – ${dayjs(end).format(
                'DD MMM, YYYY'
              )}`
              : '';

          return (
            <>
              {/* Visible single field (anchor) */}
              <TextField
                ref={anchorRef}
                size="small"
                fullWidth
                label={floatingLabel}
                value={displayValue}
                error={!!error}
                helperText={error?.message}
                placeholder={placeholder}
                slotProps={{
                  input: {
                    readOnly: true,
                    notched: true,
                    endAdornment: endIcon ? (
                      <InputAdornment position="end">
                        {endIcon}
                      </InputAdornment>
                    ) : undefined,
                    startAdornment: startIcon ? (
                      <InputAdornment position="start">
                        {startIcon}
                      </InputAdornment>
                    ) : undefined,
                  },
                  inputLabel: {
                    shrink: true,
                    required: required,
                    className: globalStyles.floatingLabelText,
                  },
                }}
                onClick={() => setOpenStart(true)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpenStart(true);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                    cursor: 'pointer',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8.5px 14px',
                    fontSize: '15px',
                    fontFamily: fonts.neue,
                    fontWeight: 500,
                    color: colors.black,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.secondary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.secondary,
                  },
                  '& input::placeholder': {
                    fontSize: '14px',
                    opacity: 1,
                    fontFamily: fonts.neue,
                    fontWeight: 500,
                    color: colors.secondary,
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                  },
                  ...(startIcon && {
                    '& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input':
                    {
                      paddingLeft: 0.5,
                    },
                  }),

                  ...(endIcon && {
                    '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
                      paddingRight: 1,
                    },
                  }),

                  ...sx,
                }}
              />

              {/* Start picker (calendar only) */}
              <DatePicker
                open={openStart}
                value={start}
                onClose={() => setOpenStart(false)}
                onChange={(value) => {
                  field.onChange([value, end]);
                  setOpenStart(false);
                  setOpenEnd(true);
                }}
                format="DD MMM, YYYY"
                slots={{ textField: () => null }}
                slotProps={{
                  popper: {
                    anchorEl: anchorRef.current,
                  },
                }}
                enableAccessibleFieldDOMStructure={false}
              />

              {/* End picker (calendar only) */}
              <DatePicker
                open={openEnd}
                value={end}
                minDate={start ?? undefined}
                onClose={() => setOpenEnd(false)}
                onChange={(value) => {
                  field.onChange([start, value]);
                  setOpenEnd(false);
                }}
                format="DD MMM, YYYY"
                slots={{ textField: () => null }}
                slotProps={{
                  popper: {
                    anchorEl: anchorRef.current,
                  },
                }}
                enableAccessibleFieldDOMStructure={false}
              />

            </>
          );
        }}
      />
    </Box>
  );
}
